import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart3, Loader2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { showErrorToast, showSuccessToast } from '@/utils/error-handler';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { useJobStore } from '@/store/jobStore';
import { useQuery } from '@tanstack/react-query';
import { datasetsApi } from '@/services/api/datasets';
import apiClient from '@/services/api/client';
import { useScriptStore } from '@/store/scriptStore';
import { timeframeToSeconds } from '@/types/backtest';
import type { StrategyMetadata } from '@/types/backtest';

interface BacktestButtonProps {
  scriptId: string;
  selectedDatasetId?: string;
  variant?: 'default' | 'outline' | 'ghost';
  disabled?: boolean;
  hasUnsavedChanges?: boolean;
  isNew?: boolean;
  onSaveBeforeRun?: () => Promise<string>;
  onJobCreated?: (jobId: string) => void;
  size?: 'default' | 'sm' | 'lg' | 'icon';
  className?: string;
}

export default function BacktestButton({
  scriptId,
  selectedDatasetId: propSelectedDatasetId = '',
  variant = 'default',
  disabled = false,
  hasUnsavedChanges = false,
  isNew = false,
  onSaveBeforeRun,
  onJobCreated,
  size = 'default',
  className,
}: BacktestButtonProps) {
  const navigate = useNavigate();
  const { createJob } = useJobStore();
  const { currentScript } = useScriptStore();
  const [open, setOpen] = useState(false);
  const [localSelectedDatasetId, setLocalSelectedDatasetId] = useState<string>(propSelectedDatasetId);
  const [isRunning, setIsRunning] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const selectedDatasetId = propSelectedDatasetId || localSelectedDatasetId;

  const { data: datasetsData } = useQuery({
    queryKey: ['datasets'],
    queryFn: async () => {
      const result = await datasetsApi.list()
      return result.datasets
    },
  });

  const readyDatasets = datasetsData?.filter((d) => d.status === 'ready') || [];

  const handleRunBacktest = async (datasetId?: string) => {
    const finalDatasetId = datasetId || selectedDatasetId;
    if (!finalDatasetId) {
      setOpen(true);
      return;
    }

    // Validate that the selected dataset still exists
    const datasetExists = readyDatasets.some(d => d.id === finalDatasetId);
    if (!datasetExists) {
      showErrorToast(new Error('Selected dataset no longer exists. Please select a different dataset.'));
      return;
    }

    // Check if backtest configuration exists
    const strategyMetadata = currentScript?.strategyMetadata as StrategyMetadata | undefined;
    if (!strategyMetadata || !strategyMetadata.signals) {
      showErrorToast(
        new Error('Backtest configuration not found. Please configure backtest settings first.'),
        'Configure backtest before running'
      );
      return;
    }

    // Validate that at least one entry signal is configured
    const hasEntrySignals = 
      (strategyMetadata.signals.entry.long && strategyMetadata.signals.entry.long.length > 0) ||
      (strategyMetadata.signals.entry.short && strategyMetadata.signals.entry.short.length > 0);
    
    if (!hasEntrySignals) {
      showErrorToast(
        new Error('No entry signals configured. Please configure at least one entry signal (long or short).'),
        'Invalid backtest configuration'
      );
      return;
    }

    setIsRunning(true);
    setIsSaving(false);

    try {
      let finalScriptId = scriptId;

      // Save script first if needed
      if ((hasUnsavedChanges || isNew) && onSaveBeforeRun) {
        setIsSaving(true);
        try {
          finalScriptId = await onSaveBeforeRun();
          if (!finalScriptId || finalScriptId === 'new') {
            throw new Error('Failed to save script before backtest');
          }
        } catch (error) {
          console.error('Failed to save script before backtest:', error);
          setIsRunning(false);
          setIsSaving(false);
          return;
        }
        setIsSaving(false);
      }

      // Validate scriptId
      if (!finalScriptId || finalScriptId === 'new') {
        throw new Error('Script must be saved before running backtest');
      }

      // Validate script exists
      try {
        await apiClient.get(`/scripts/${finalScriptId}`);
      } catch (error: any) {
        if (error.response?.status === 404) {
          throw new Error('Script no longer exists. Please save your script first.');
        } else if (error.response?.status === 403) {
          throw new Error('You do not have access to this script.');
        } else {
          throw new Error('Script validation failed. Please try again.');
        }
      }

      // Build backtest config from strategyMetadata
      const savedConfig = currentScript?.strategyMetadata as any;
      
      // Signal Dataset: ALWAYS use script's selectedDatasetId (auto-synced)
      // This ensures consistency - signal dataset always matches what's selected in the script
      const signalDatasetId = currentScript?.selectedDatasetId || finalDatasetId;
      
      // Execution Dataset: Use saved config if available, otherwise use signal dataset
      // If useSeparateExecutionDataset is false, executionDatasetId should be undefined
      const executionDatasetId = savedConfig.executionDatasetId || signalDatasetId;
      
      // Get selected datasets to extract timeframes (for legacy support)
      const signalDataset = readyDatasets.find(d => d.id === signalDatasetId);
      const executionDataset = readyDatasets.find(d => d.id === executionDatasetId);
      
      const backtestConfig = {
        initialCapital: savedConfig.initialCapital || 10000,
        strategyMetadata: strategyMetadata,
        signalDatasetId,
        executionDatasetId,
        // Include templateId if saved in script metadata
        ...(savedConfig.templateId && { templateId: savedConfig.templateId }),
        // Include timeframes for legacy support (will be overridden by backend from datasets)
        signalTimeframeSeconds: signalDataset?.timeframe 
          ? timeframeToSeconds(signalDataset.timeframe) 
          : (savedConfig.signalTimeframe ? timeframeToSeconds(savedConfig.signalTimeframe) : 300),
        executionTimeframeSeconds: executionDataset?.timeframe 
          ? timeframeToSeconds(executionDataset.timeframe) 
          : (savedConfig.executionTimeframe ? timeframeToSeconds(savedConfig.executionTimeframe) : 60),
        positionSize: savedConfig.positionSize || 1,
        closeEndOfDay: savedConfig.closeEndOfDay ?? false,
        ...(savedConfig.leverage !== undefined && { leverage: savedConfig.leverage }),
        ...(savedConfig.marginRequirement !== undefined && { marginRequirement: savedConfig.marginRequirement }),
        // Include any other saved config
        ...(savedConfig.commission && { commission: savedConfig.commission }),
        ...(savedConfig.slippage && { slippage: savedConfig.slippage }),
      };

      // Same shape as run script (execute): pass options so backend runs script with same logic, then engine vela a vela
      const options: Record<string, unknown> = {};
      const dateFilter = savedConfig?.dateFilter;
      if (dateFilter?.enabled && (dateFilter?.startDate || dateFilter?.endDate)) {
        options.dateFilter = {
          startDate: dateFilter.startDate,
          endDate: dateFilter.endDate,
        };
      }

      // Create backtest job (options applied in worker like execute-worker, then engine candle-by-candle)
      const job = await createJob('backtest', {
        scriptId: finalScriptId,
        datasetId: finalDatasetId, // Legacy: main dataset
        ...backtestConfig,
        options,
      });

      setOpen(false);
      showSuccessToast('Backtest job created successfully');

      // Save to localStorage
      localStorage.setItem('lastSelectedDatasetId', finalDatasetId);

      // Call callback if provided
      if (onJobCreated) {
        onJobCreated(job.id);
      } else {
        // Default behavior: navigate to job page
        navigate(`/jobs/${job.id}`);
      }
    } catch (error) {
      console.error('Failed to run backtest:', error);
      showErrorToast(error, 'Failed to run backtest. Please try again.');
    } finally {
      setIsRunning(false);
      setIsSaving(false);
    }
  };

  const handleClick = () => {
    if (selectedDatasetId) {
      handleRunBacktest();
    } else {
      setOpen(true);
    }
  };

  const hasConfig = currentScript?.strategyMetadata && 
    (currentScript.strategyMetadata as any)?.signals?.entry;

  return (
    <>
      <Button
        variant={variant}
        size={size}
        disabled={disabled || !hasConfig || (!selectedDatasetId && readyDatasets.length === 0)}
        onClick={handleClick}
        className={className}
      >
        {isSaving ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Saving...
          </>
        ) : isRunning ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Running...
          </>
        ) : (
          <>
            <BarChart3 className="w-4 h-4 mr-2" />
            {hasUnsavedChanges || isNew ? 'Save & Backtest' : 'Run Backtest'}
          </>
        )}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {hasUnsavedChanges || isNew ? 'Save & Run Backtest' : 'Run Backtest'}
            </DialogTitle>
            <DialogDescription>
              {(hasUnsavedChanges || isNew) && (
                <span className="text-amber-600 dark:text-amber-400 block mb-2">
                  ⚠️ Your script will be saved before running backtest
                </span>
              )}
              {!hasConfig && (
                <span className="text-red-600 dark:text-red-400 block mb-2 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  Backtest configuration required. Please configure backtest settings first.
                </span>
              )}
              Select a dataset to run backtest on
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="backtest-dataset">Dataset</Label>
              <Select value={localSelectedDatasetId} onValueChange={setLocalSelectedDatasetId}>
                <SelectTrigger id="backtest-dataset">
                  <SelectValue placeholder="Select a dataset" />
                </SelectTrigger>
                <SelectContent>
                  {readyDatasets.length === 0 ? (
                    <div className="p-2 text-sm text-muted-foreground">
                      No datasets available
                    </div>
                  ) : (
                    readyDatasets.map((dataset) => (
                      <SelectItem key={dataset.id} value={dataset.id}>
                        {dataset.name} {dataset.rowCount != null ? `(${dataset.rowCount.toLocaleString()} rows)` : ''}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => handleRunBacktest(localSelectedDatasetId)}
              disabled={!localSelectedDatasetId || !hasConfig || isRunning || isSaving}
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : isRunning ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Running...
                </>
              ) : (
                <>
                  <BarChart3 className="w-4 h-4 mr-2" />
                  {hasUnsavedChanges || isNew ? 'Save & Run Backtest' : 'Run Backtest'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
